import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue";
import { Badge, Button, Dropdown, RadioButton, RecordsTable, Tooltip, WidgetShell, } from "@eudonet/opal";
import { Fragment, computed, defineComponent, h, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { addressBadgeTheme, findPrimaryId } from "../../domain/address-utils";
import { useAddressFormatter } from "@/shared/composables/useAddressFormatter";
export default /*@__PURE__*/ _defineComponent({
    __name: 'AddressesWidget',
    props: {
        addresses: {},
        canEdit: { type: Boolean, default: false }
    },
    emits: ["add", "expand", "edit", "delete", "update:primaryId"],
    setup(__props, { emit: __emit }) {
        const TruncatedCell = defineComponent({
            props: { text: { type: String, required: true } },
            setup(props) {
                const spanRef = ref(null);
                const isTruncated = ref(false);
                let observer = null;
                onMounted(() => {
                    if (spanRef.value) {
                        observer = new ResizeObserver(() => {
                            if (spanRef.value) {
                                isTruncated.value = spanRef.value.scrollWidth > spanRef.value.offsetWidth;
                            }
                        });
                        observer.observe(spanRef.value);
                    }
                });
                onBeforeUnmount(() => {
                    observer?.disconnect();
                });
                return () => h(Tooltip, { text: props.text, disabled: !isTruncated.value, style: "display: block; width: 100%;" }, () => h("span", {
                    ref: spanRef,
                    class: "block text-sm text-(--text-text-black-default) truncate",
                }, props.text));
            },
        });
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const addressFormatter = useAddressFormatter();
        const primaryId = computed(() => findPrimaryId(props.addresses));
        const columns = computed(() => {
            const id = primaryId.value;
            const base = [
                {
                    id: "address",
                    header: t("organisation.detail.addresses.columns.fullAddress"),
                    cell: (row) => h(TruncatedCell, { text: addressFormatter.format(row) }),
                    width: "min-w-40",
                },
                {
                    id: "type",
                    header: t("organisation.detail.addresses.columns.type"),
                    width: "w-46",
                    sortable: true,
                    sortBy: (row) => row.type,
                    cell: (row) => h(Badge, {
                        theme: addressBadgeTheme(row.type),
                        label: t(`organisation.detail.addresses.types.${row.type}`),
                        showCloseIcon: false,
                        size: "sm",
                        truncate: false,
                    }),
                },
                {
                    id: "isPrimary",
                    header: t("organisation.detail.addresses.columns.isPrimary"),
                    width: "w-28",
                    sortable: true,
                    sortBy: (row) => (row.id === id ? "0" : "1"),
                    cell: (row) => h(Fragment, null, [
                        h("label", {
                            for: `radio-primary-${row.id}`,
                            class: "sr-only",
                        }, t("organisation.detail.addresses.columns.setPrimaryAriaLabel", {
                            address: addressFormatter.format(row),
                        })),
                        h(RadioButton, {
                            id: `radio-primary-${row.id}`,
                            modelValue: id,
                            value: row.id,
                            name: "primary-address",
                            disabled: !props.canEdit,
                            "onUpdate:modelValue": (newId) => emit("update:primaryId", newId),
                        }),
                    ]),
                },
            ];
            if (props.canEdit) {
                base.push({
                    id: "actions",
                    header: "",
                    width: "w-18",
                    cell: (row) => h(Dropdown, {
                        ghost: true,
                        label: "",
                        triggerIconLeft: "ri-more-line",
                        menuAlign: "right",
                        ariaLabel: t("organisation.detail.addresses.rowActions"),
                        items: [
                            {
                                label: t("organisation.detail.addresses.actions.edit"),
                                icon: "ri-pencil-line",
                                onClick: () => emit("edit", row),
                            },
                            {
                                label: t("organisation.detail.addresses.actions.delete"),
                                icon: "ri-delete-bin-line",
                                danger: true,
                                onClick: () => emit("delete", row),
                            },
                        ],
                    }),
                });
            }
            return base;
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(WidgetShell), {
                header: _ctx.$t('organisation.detail.addresses.title')
            }, {
                actions: _withCtx(() => [
                    _createVNode(_unref(Tooltip), {
                        text: _ctx.$t('common.permissions.noEditRights'),
                        disabled: __props.canEdit
                    }, {
                        default: _withCtx(() => [
                            _createVNode(_unref(Button), {
                                label: _ctx.$t('organisation.detail.addresses.addAddress'),
                                "icon-left": "ri-add-line",
                                disabled: !__props.canEdit,
                                "data-testid": "org-add-address",
                                onClick: _cache[0] || (_cache[0] = ($event) => (_ctx.$emit('add')))
                            }, null, 8 /* PROPS */, ["label", "disabled"])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["text", "disabled"]),
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        "icon-only": "",
                        "icon-left": "ri-expand-diagonal-fill",
                        "aria-label": _ctx.$t('organisation.detail.addresses.expand'),
                        onClick: _cache[1] || (_cache[1] = ($event) => (_ctx.$emit('expand')))
                    }, null, 8 /* PROPS */, ["aria-label"])
                ]),
                default: _withCtx(() => [
                    _createVNode(_unref(RecordsTable), {
                        columns: columns.value,
                        data: __props.addresses,
                        "table-class": 'min-w-130',
                        "empty-text": _ctx.$t('organisation.detail.addresses.empty')
                    }, null, 8 /* PROPS */, ["columns", "data", "empty-text"])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["header"]));
        };
    }
});
